<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <router-link to="/admissions/movie-tag" class="mr-3">
                        <icon name="chevron-left"/> Back
                    </router-link>
                    <strong>Per movie tag</strong>
                    <span class="text-muted ml-2">— cinema year <strong>{{ year }}</strong>, Brightfish preshow admissions (sales house BF)</span>
                </div>
                <div class="form-inline">
                    <router-link v-if="parseInt(year, 10) > firstTagYear" :to="'/admissions/movie-tag/' + (parseInt(year, 10) - 1)" class="btn btn-sm btn-outline-secondary mr-2">
                        <icon name="chevron-left"/> {{ parseInt(year, 10) - 1 }}
                    </router-link>
                    <router-link :to="'/admissions/movie-tag/' + (parseInt(year, 10) + 1)" class="btn btn-sm btn-outline-secondary">
                        {{ parseInt(year, 10) + 1 }} <icon name="chevron-right"/>
                    </router-link>
                </div>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="parseInt(year, 10) < firstTagYear" class="box">
            <div class="box-content text-center text-muted py-4">Movie tags are only available from cinema year {{ firstTagYear }}.</div>
        </div>

        <div v-else-if="!totals.weeks" class="box">
            <div class="box-content text-center text-muted py-4">No Brightfish admissions in {{ year }}.</div>
        </div>

        <template v-else>
            <div class="box">
                <div class="box-content">
                    <heading upper="Per tag" :lower="rows.length + ' tags, ' + formatInt(totals.admissions) + ' admissions over ' + totals.weeks + ' weeks'" border/>
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th>Movie tag</th>
                                <th class="text-right shx-th-100"># Movies</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="text-right">Preshow (BF)</th>
                                <th class="text-right shx-th-100">% of year</th>
                                <th class="text-right shx-th-120">Avg / movie</th>
                                <th class="shx-th-200"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="tag in rows" :key="tag.movie_tag || 'none'">
                                <td>
                                    <a :href="'#tag-' + slug(tag.movie_tag)" :class="{ 'text-muted': !tag.movie_tag }">{{ tagLabel(tag.movie_tag) }}</a>
                                </td>
                                <td class="text-right">{{ tag.movies }}</td>
                                <td class="text-right">{{ tag.weeks }}</td>
                                <td class="text-right"><strong>{{ formatInt(tag.admissions) }}</strong></td>
                                <td class="text-right">{{ tag.pct !== null ? tag.pct + '%' : '—' }}</td>
                                <td class="text-right">{{ tag.movies ? formatInt(tag.admissions / tag.movies) : '—' }}</td>
                                <td>
                                    <div class="progress" style="height: 8px;" :title="(tag.pct !== null ? tag.pct : 0) + '%'">
                                        <div class="progress-bar" role="progressbar" :style="{ width: (tag.pct || 0) + '%' }"></div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td><strong>Total</strong></td>
                                <td class="text-right"><strong>{{ totals.movies }}</strong></td>
                                <td class="text-right"><strong>{{ totals.weeks }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(totals.admissions) }}</strong></td>
                                <td class="text-right"><strong>100%</strong></td>
                                <td class="text-right"><strong>{{ totals.movies ? formatInt(totals.admissions / totals.movies) : '—' }}</strong></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                    <p class="text-muted small mt-2 mb-0">
                        Only rows with <code>saleshouse = BF</code> of cinema year {{ year }} count.
                        <em>{{ tagLabel(null) }}</em> collects titles without a Movix id, without a history row or without a tag;
                        their admissions count, but only identified movies are listed below.
                        Same data as JSON: <code>GET /api/v1/admissions/movie-tag/{{ year }}</code>.
                    </p>
                </div>
            </div>

            <div v-for="tag in rows" :key="'top-' + (tag.movie_tag || 'none')" :id="'tag-' + slug(tag.movie_tag)" class="box">
                <div class="box-content">
                    <heading :upper="tagLabel(tag.movie_tag)"
                             :lower="'Top ' + tag.top.length + ' of ' + tag.movies + ' movies, ' + formatInt(tag.admissions) + ' admissions (' + (tag.pct !== null ? tag.pct : 0) + '% of ' + year + ')'"
                             border/>
                    <table v-if="tag.top.length" class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th class="shx-th-50">#</th>
                                <th class="shx-th-80">Movix</th>
                                <th>Title</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="text-right">Preshow (BF)</th>
                                <th class="text-right shx-th-100">% of tag</th>
                                <th class="text-right shx-th-100">% of year</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="movie in tag.top" :key="movie.movix_id">
                                <td class="text-muted">{{ movie.rank }}</td>
                                <td>
                                    <router-link :to="'/history/' + movie.movix_id">{{ movie.movix_id }}</router-link>
                                </td>
                                <td>{{ movie.title || '—' }}</td>
                                <td class="text-right">{{ movie.weeks }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions) }}</td>
                                <td class="text-right">{{ movie.pct !== null ? movie.pct + '%' : '—' }}</td>
                                <td class="text-right">{{ movie.pct_year !== null ? movie.pct_year + '%' : '—' }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="4"><strong>Top {{ tag.top.length }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(topAdmissions(tag)) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(topAdmissions(tag), tag.admissions) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(topAdmissions(tag), totals.admissions) }}</strong></td>
                            </tr>
                        </tfoot>
                    </table>
                    <p v-else class="text-muted small mb-0">No identified movies.</p>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';

export default {
    name: 'AdmissionsMovieTagYear',
    components: {
        Heading,
    },
    props: ['year'],
    data() {
        return {
            loading: true,
            bucket: {},
            totals: {},
            rows: [],
            firstTagYear: 2024,
        };
    },
    created() {
        this.load();
    },
    watch: {
        year() {
            this.load();
        },
    },
    methods: {
        load() {
            if (parseInt(this.year, 10) < this.firstTagYear) {
                this.totals = {};
                this.rows = [];
                this.loading = false;
                return;
            }
            this.loading = true;
            axios
                .get(`/admissions/movie-tag/${this.year}`)
                .then(response => {
                    this.bucket = response.data.data.bucket || {};
                    this.totals = response.data.data.totals || {};
                    this.rows = response.data.data.rows || [];
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        topAdmissions(tag) {
            return (tag.top || []).reduce((sum, m) => sum + (m.admissions || 0), 0);
        },
        tagLabel(tag) {
            return tag || 'no tag';
        },
        slug(tag) {
            return (tag || 'none').replace(/[^a-z0-9]+/gi, '-');
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
